import { useEffect, useMemo, useRef, useState } from "react";
import type { FixedCost, Row } from "../types.ts";
import { formatAmount, todayISO } from "../format.ts";
import {
  isValidMonth,
  planFixedCostImport,
  rowsToAdd,
  type InLedger,
} from "../fixedCostImport.ts";
import { Modal } from "./Modal.tsx";

type Props = {
  fixedCosts: FixedCost[];
  rows: Row[];
  filterLabel: string | null;
  onConfirm: (rows: Row[], month: string) => void;
  onCancel: () => void;
};

const IN_LEDGER_NOTE: Record<InLedger, string | null> = {
  no: null,
  active: "finns redan",
  snoozed: "finns redan, snoozad",
};

export function FixedCostImportDialog({
  fixedCosts,
  rows,
  filterLabel,
  onConfirm,
  onCancel,
}: Props) {
  const monthRef = useRef<HTMLInputElement>(null);
  const [month, setMonth] = useState(() => todayISO().slice(0, 7));
  const [toggled, setToggled] = useState<ReadonlySet<string>>(() => new Set());
  const validMonth = isValidMonth(month);
  const candidates = useMemo(
    () => (validMonth ? planFixedCostImport(fixedCosts, rows, month) : []),
    [fixedCosts, rows, month, validMonth],
  );
  const selected = useMemo(
    () =>
      new Set(
        candidates
          .filter((c) => (c.inLedger === "no") !== toggled.has(c.row.id))
          .map((c) => c.row.id),
      ),
    [candidates, toggled],
  );
  const chosen = candidates.filter((c) => selected.has(c.row.id));
  const count = chosen.length;
  const sum = chosen.reduce((a, c) => a + c.row.amount, 0);

  const toggle = (id: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  useEffect(() => {
    monthRef.current?.focus();
  }, []);

  return (
    <Modal labelledBy="fixed-cost-import-title" onClose={onCancel}>
      <h2 id="fixed-cost-import-title">Månadstransport</h2>
      {filterLabel && <p className="modal-subtitle">{filterLabel}</p>}

      <label className="month-field">
        <span>Månad</span>
        <input
          ref={monthRef}
          type="month"
          value={month}
          aria-invalid={!validMonth}
          onChange={(e) => setMonth(e.currentTarget.value)}
        />
      </label>

      {validMonth ? (
        <ul className="import-summary">
          <li>
            <strong>{count}</strong> av {candidates.length} valda (
            {formatAmount(sum)})
          </li>
        </ul>
      ) : (
        <p className="month-error">Ange månad som ÅÅÅÅ-MM</p>
      )}

      {candidates.length > 0 && (
        <div className="import-list">
          <table>
            <tbody>
              {candidates.map((c) => {
                const note = IN_LEDGER_NOTE[c.inLedger];
                return (
                  <tr
                    key={c.row.id}
                    className={selected.has(c.row.id) ? "" : "unselected"}
                  >
                    <td className="import-check">
                      <input
                        type="checkbox"
                        aria-label={`Ta med ${c.row.supplier || "kostnad"} ${c.row.dueDate}`}
                        checked={selected.has(c.row.id)}
                        onChange={() => toggle(c.row.id)}
                      />
                    </td>
                    <td className="import-date">{c.row.dueDate}</td>
                    <td className="import-supplier">
                      {c.row.supplier}
                      {note && <span className="import-note">{note}</span>}
                    </td>
                    <td
                      className={`import-amount${c.row.amount > 0 ? " import-amount-pos" : ""}`}
                    >
                      {formatAmount(c.row.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="modal-actions">
        <button type="button" onClick={onCancel}>
          Avbryt
        </button>
        <button
          type="button"
          className="primary"
          disabled={count === 0}
          onClick={() => onConfirm(rowsToAdd(candidates, selected), month)}
        >
          {count === 0
            ? "Inget att lägga in"
            : `Lägg till ${count} rad${count === 1 ? "" : "er"}`}
        </button>
      </div>
    </Modal>
  );
}
